import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bus, Home, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  TRIP_LEGS,
  TRIP_LEG_LABEL_KEY,
  TRIP_STATUS_PRESENTATION,
  isTripLeg,
  type TripLeg,
  type TripStatus,
} from '@/lib/transport/types';

// MANUAL ground-transport dispatch (feat/transport §5 T5 / §7).
//
// This dialog spends the airline's money on the operator's say-so, overriding the
// AUTOMATIC path, so three things are deliberate:
//
//  1. `reason` is REQUIRED. It is the only record of WHY the automatic path was
//     overridden, and the BFF rejects a dispatch without it (400 `reason_required`)
//     — validating here means the operator finds out before the round trip, not
//     after.
//  2. `leg` starts UNSET. Defaulting it would let a mis-click book a car in the
//     wrong direction; picking a direction is the operator's decision to make.
//  3. Passengers are individually selectable. The card offers a group-wide
//     dispatch AND a per-passenger one — both land here, the second with that one
//     passenger pre-selected. Dispatch is one trip per passenger (brief §9.4: no
//     vehicle splitting in M1).
//
// Mount it only while it is open (the parent keys it on the intent), so every open
// starts from clean state instead of whatever the last dispatch left behind.

export interface DispatchDialogPassenger {
  paxUrn: string;
  /** Already-masked roster name — no reveal here (PII stays on PassengersCard). */
  fullName: string;
  /** The passenger's live trip status, when they already have one. */
  liveStatus?: TripStatus | undefined;
}

export interface DispatchDialogSubmit {
  passengerUrns: string[];
  leg: TripLeg;
  /** ISO-8601, only when the operator scheduled it. */
  scheduledAt?: string;
  reason: string;
}

/**
 * The ride-home destination as this dialog may show it — the coarse label only.
 * Present ⇒ `AIRPORT_TO_HOME` is offered in the leg picker and, once chosen, the
 * read-only drop-off line names it. The point itself is the card's business
 * (it goes on the wire, never on screen).
 */
export interface DispatchDialogHome {
  label?: string | undefined;
}

interface TransportDispatchDialogProps {
  /** Friendly PNR label for the confirm copy. */
  groupLabel: string;
  /**
   * Offer the `AIRPORT_TO_HOME` leg. Only a stay-home (`LOCAL_RESIDENT`) group
   * with a geocoded destination passes this — a hotel group never sees the leg,
   * because this generic picker has no way to supply the drop-off a home ride needs.
   */
  allowHome?: DispatchDialogHome | null | undefined;
  passengers: readonly DispatchDialogPassenger[];
  /** Pre-selected passengers (per-passenger dispatch / re-dispatch of a failed trip). */
  initialPassengerUrns?: readonly string[];
  /** Pre-selected leg — set when re-dispatching, so the retry matches the failure. */
  initialLeg?: TripLeg | null;
  /** True while the dispatch mutation is in flight. */
  pending: boolean;
  /** Server-side failure, surfaced under the form. */
  error?: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: DispatchDialogSubmit) => void;
}

export function TransportDispatchDialog({
  groupLabel,
  allowHome,
  passengers,
  initialPassengerUrns,
  initialLeg,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: TransportDispatchDialogProps) {
  const { t } = useTranslation('pnrActions');
  const [selected, setSelected] = useState<string[]>(() =>
    initialPassengerUrns && initialPassengerUrns.length > 0
      ? [...initialPassengerUrns]
      : passengers.map((p) => p.paxUrn),
  );
  // A home leg pre-filled for a group that can no longer take one (the stay-home
  // declaration was undone between a failed ride and its re-dispatch) starts
  // UNSET like any other — never a leg the picker cannot show.
  const [leg, setLeg] = useState<TripLeg | ''>(
    initialLeg === 'AIRPORT_TO_HOME' && !allowHome ? '' : (initialLeg ?? ''),
  );
  const [scheduledAtLocal, setScheduledAtLocal] = useState('');
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const toggle = (paxUrn: string): void => {
    setSelected((prev) =>
      prev.includes(paxUrn) ? prev.filter((p) => p !== paxUrn) : [...prev, paxUrn],
    );
  };

  const submit = (): void => {
    if (selected.length === 0) {
      setValidation(t('dispatch.validation.noPassenger'));
      return;
    }
    if (!isTripLeg(leg)) {
      setValidation(t('dispatch.validation.noLeg'));
      return;
    }
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidation(t('dispatch.validation.noReason'));
      return;
    }
    // `datetime-local` has no timezone; the browser reads it in the operator's own
    // zone, which is the one they typed it in. Convert to the ISO instant the seam
    // wants rather than shipping a naive local string the domain would misread.
    let scheduledAt: string | undefined;
    if (scheduledAtLocal) {
      const parsed = new Date(scheduledAtLocal);
      if (Number.isNaN(parsed.getTime())) {
        setValidation(t('dispatch.validation.badTime'));
        return;
      }
      scheduledAt = parsed.toISOString();
    }
    setValidation(null);
    onConfirm({
      passengerUrns: selected,
      leg,
      reason: trimmed,
      ...(scheduledAt ? { scheduledAt } : {}),
    });
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] overflow-y-auto"
        data-testid="transport-dispatch-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="h-4 w-4" aria-hidden /> {t('dispatch.title')}
          </DialogTitle>
          <DialogDescription>
            {t('dispatch.descriptionPrefix')}{' '}
            <span className="font-medium">{groupLabel}</span>{' '}
            {t('dispatch.descriptionSuffix')}
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-1.5">
          <legend className="mb-1 text-sm font-medium">
            {t('dispatch.passengersLegend', { n: selected.length })}
          </legend>
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
            {passengers.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('dispatch.noRoster')}</p>
            ) : (
              passengers.map((p) => (
                <label
                  key={p.paxUrn}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted"
                  title={p.paxUrn}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(p.paxUrn)}
                    onChange={() => toggle(p.paxUrn)}
                    className="h-4 w-4 shrink-0 accent-primary"
                    data-testid={`transport-dispatch-pax-${p.paxUrn}`}
                  />
                  <span className="min-w-0 flex-1 truncate">{p.fullName}</span>
                  {p.liveStatus ? (
                    <Badge variant="info" size="sm">
                      {t(TRIP_STATUS_PRESENTATION[p.liveStatus].labelKey)}
                    </Badge>
                  ) : null}
                </label>
              ))
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">{t('dispatch.duplicateNote')}</p>
        </fieldset>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('dispatch.legLabel')}</span>
            <select
              value={leg}
              onChange={(e) => setLeg(e.target.value as TripLeg | '')}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              data-testid="transport-dispatch-leg"
            >
              <option value="">{t('dispatch.legPlaceholder')}</option>
              {/* The home leg is offered ONLY with `allowHome` — a home ride needs the
                  geocoded drop-off this generic picker cannot supply, so the card's
                  "Ride home" action opens this dialog pre-filled for a stay-home group,
                  and a hotel group never sees the leg at all. */}
              {TRIP_LEGS.filter((value) => value !== 'AIRPORT_TO_HOME' || allowHome).map((value) => (
                <option key={value} value={value}>
                  {t(TRIP_LEG_LABEL_KEY[value])}
                </option>
              ))}
            </select>
          </label>

          {leg === 'AIRPORT_TO_HOME' && allowHome ? (
            <div className="text-sm sm:col-span-2" data-testid="transport-dispatch-dropoff-home">
              <span className="mb-1 block font-medium">{t('dispatch.dropoffLabel')}</span>
              <p className="flex items-center gap-1.5 rounded-md border border-border bg-muted/30 p-2">
                <Home className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span>
                  {allowHome.label
                    ? t('dispatch.dropoffHome', { label: allowHome.label })
                    : t('dispatch.dropoffHomeNoLabel')}
                </span>
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">{t('dispatch.dropoffHomeHint')}</p>
            </div>
          ) : null}

          <label className="block text-sm">
            <span className="mb-1 block font-medium">
              {t('dispatch.pickupTimeLabel')}{' '}
              <span className="font-normal text-muted-foreground">{t('dispatch.optional')}</span>
            </span>
            <input
              type="datetime-local"
              value={scheduledAtLocal}
              onChange={(e) => setScheduledAtLocal(e.target.value)}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              data-testid="transport-dispatch-scheduled-at"
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('reason.requiredLabel')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t('dispatch.reasonPlaceholder')}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
            data-testid="transport-dispatch-reason"
          />
        </label>

        {validation ? (
          <p className="text-xs text-destructive" role="alert" data-testid="transport-dispatch-validation">
            {validation}
          </p>
        ) : null}
        {error ? (
          <p className="text-xs text-destructive" role="alert" data-testid="transport-dispatch-error">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t('common:common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={pending}
            onClick={submit}
            data-testid="transport-dispatch-confirm"
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {t('dispatch.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
